// ─── Soltar algo sobre la timeline ──────────────────────────────────────────
// Arrastrar una ficha de Archivos (o un archivo de la computadora) y soltarla
// sobre la timeline tiene que caer DONDE se soltó: el tiempo sale de la X y la
// capa de la Y. La regla de la Y es la MISMA que usa mover un clip entre capas
// (`handleTimelinePointerMove` en `VideoEditor.tsx`): la franja central de una
// fila donde ese tipo puede vivir es "metelo en esta capa"; el resto —los
// huecos entre filas, una fila de otra familia, arriba de la regla, abajo de
// todo— es "abrí una capa nueva acá".
//
// Es matemática pura y vive afuera del componente para poder testearla.
// Ver ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta.

/** Las tres familias de pistas, cada una con su numeración de `trackIndex`. */
export type FamiliaDePista = "video" | "audio" | "elements";

export interface FilaDeLaTimeline {
  familia: FamiliaDePista;
  /** El `trackIndex` de la capa. La fila de subtítulos no lo tiene: no recibe
   *  nada soltado. */
  indice?: number;
}

export interface GeometriaDeFilas {
  /** Alto de la regla de tiempo, arriba de la primera fila. */
  rulerH: number;
  rowH: number;
  /** Separación entre filas (y entre la regla y la primera). */
  gap: number;
}

export type DestinoDeSoltar =
  /** Meterlo en una capa que ya existe. `yPx` es el techo de esa fila. */
  | { clase: "dentro"; trackIndex: number; yPx: number }
  /** Abrir una capa nueva en el hueco `posicion` (0 = arriba de todo, N = abajo
   *  de la última). `yPx` es dónde se dibuja la línea. */
  | { clase: "hueco"; posicion: number; yPx: number };

/** La franja central de una fila, en fracción de su alto. Es generosa porque
 *  apuntarle a una fila es más difícil que apuntarle a un hueco, que se lleva
 *  toda la separación (mismo criterio que mover un clip). */
const CENTRO_DESDE = 0.22;
const CENTRO_HASTA = 0.78;

export function destinoDeSoltar(params: {
  /** La Y en coordenadas del CONTENIDO de la timeline (con el scroll sumado). */
  y: number;
  /** Las filas en el orden en que se dibujan. */
  filas: FilaDeLaTimeline[];
  /** De qué familia es lo que se suelta. */
  familia: FamiliaDePista;
  geometria: GeometriaDeFilas;
}): DestinoDeSoltar {
  const { y, filas, familia, geometria } = params;
  const paso = geometria.rowH + geometria.gap;
  const topPrimeraFila = geometria.rulerH + geometria.gap;

  const filaFlotante = (y - topPrimeraFila) / paso;
  const indiceDeFila = Math.floor(filaFlotante);
  const dentroDeLaFila = filaFlotante - indiceDeFila;
  const fila =
    indiceDeFila >= 0 && indiceDeFila < filas.length ? filas[indiceDeFila] : undefined;
  const enElCentro = dentroDeLaFila > CENTRO_DESDE && dentroDeLaFila < CENTRO_HASTA;

  if (fila && enElCentro && fila.familia === familia && typeof fila.indice === "number") {
    return {
      clase: "dentro",
      trackIndex: fila.indice,
      yPx: topPrimeraFila + indiceDeFila * paso,
    };
  }

  const posicion = Math.max(
    0,
    Math.min(filas.length, Math.round((y - topPrimeraFila) / paso)),
  );
  return {
    clase: "hueco",
    posicion,
    yPx: topPrimeraFila + posicion * paso - geometria.gap / 2,
  };
}

/** Tolerancia para los segundos en coma flotante: un hueco de 3 s entra justo un clip de 3 s. */
const EPS = 1e-6;

export interface ClipDeLaCapa {
  id: string;
  startTime: number;
  duration: number;
}

/** Dónde arranca lo que se suelta en una capa que ya tiene clips: si el punto
 *  cae ENCIMA de un clip, se pega al final de ese clip; si con eso cae encima
 *  del siguiente, sigue hasta el final de la cadena. En un hueco libre queda
 *  donde se soltó. (Lo pidió el usuario así: pegar, no empujar a los demás.)
 *  Con `duracion`, un hueco más corto que el clip nuevo cuenta como encima del
 *  siguiente: se salta al final de ése en vez de solaparlo. */
export function pegarAlFinal(params: {
  clips: ClipDeLaCapa[];
  inicio: number;
  duracion?: number;
}): number {
  let inicio = Math.max(0, params.inicio);
  const duracion = params.duracion ?? 0;
  // Cada vuelta avanza al final de un clip distinto: con `clips.length` vueltas
  // alcanza aunque estén desordenados.
  for (let i = 0; i <= params.clips.length; i++) {
    const encima = params.clips.find((c) =>
      duracion > 0
        ? c.startTime < inicio + duracion - EPS && inicio < c.startTime + c.duration
        : c.startTime <= inicio && inicio < c.startTime + c.duration,
    );
    if (!encima) break;
    inicio = encima.startTime + encima.duration;
  }
  return inicio;
}

// ─── Qué se arrastra ─────────────────────────────────────────────────────────
// Los tipos del `dataTransfer`. Durante `dragover` el navegador NO deja leer
// los datos (sólo `types`), así que la familia viaja como un tipo más —
// `application/x-atlas-familia-video`— para poder dibujar el destino antes de
// soltar.

/** Un asset del proyecto (video, audio o imagen): el id. */
export const TIPO_ASSET = "application/x-atlas-asset-id";
/** Una imagen generada con IA en esta sesión (todavía sin asset): el id. */
export const TIPO_GENERADA = "application/x-atlas-generada-id";
/** Una animación gráfica de la biblioteca del proyecto: el id. */
export const TIPO_ANIMACION = "application/x-atlas-animacion-id";
const PREFIJO_FAMILIA = "application/x-atlas-familia-";
export const TIPO_FAMILIA = (familia: FamiliaDePista): string =>
  `${PREFIJO_FAMILIA}${familia}`;

/** La familia marcada en los tipos de un arrastre, si es uno nuestro. */
export function familiaDelTransfer(types: ArrayLike<string>): FamiliaDePista | null {
  for (const t of Array.from(types)) {
    if (!t.startsWith(PREFIJO_FAMILIA)) continue;
    const f = t.slice(PREFIJO_FAMILIA.length);
    if (f === "video" || f === "audio" || f === "elements") return f;
  }
  return null;
}

const EXT_IMAGEN = /\.(png|jpe?g|webp|gif|heic|heif|avif|bmp|tiff?)$/i;

/** A qué familia va un archivo de la computadora. `null` si no es video, audio
 *  ni imagen. El HEIC en algunos Chrome llega sin MIME: se mira la extensión. */
export function familiaDeArchivo(f: { type: string; name: string }): FamiliaDePista | null {
  if (f.type.startsWith("video/")) return "video";
  if (f.type.startsWith("audio/")) return "audio";
  if (f.type.startsWith("image/") || (!f.type && EXT_IMAGEN.test(f.name))) return "elements";
  return null;
}

// ─── Qué capa le toca a lo que entra con un clic ─────────────────────────────
// Lo que se suma sin arrastrar ni pasar por el "+" de una pista arranca en el
// cursor de la timeline, y la capa se elige para no encimar: de las capas de su
// familia que están LIBRES en ese segundo, la que tiene más lugar por delante
// (hasta el próximo clip); a igual lugar, la de índice más bajo, que es la
// base. Si todas tienen un clip debajo del cursor, `null`: se abre una nueva
// (ADR-2026-09-27-lo-que-entra-con-un-clic-sigue-al-cursor).

export interface ClipConCapa extends ClipDeLaCapa {
  trackIndex: number;
}

/** Con `duracion`, la capa elegida tiene que tener ese lugar por delante: si ni
 *  la más libre alcanza, `null` (capa nueva) en vez de encimar al siguiente. */
export function capaLibreEn(params: {
  clips: ClipConCapa[];
  t: number;
  duracion?: number;
}): number | null {
  const { clips, t } = params;
  const capas = new Set(clips.map((c) => c.trackIndex));
  let mejor: { trackIndex: number; lugar: number } | null = null;
  for (const trackIndex of capas) {
    const deLaCapa = clips.filter((c) => c.trackIndex === trackIndex);
    const ocupada = deLaCapa.some((c) => c.startTime <= t && t < c.startTime + c.duration);
    if (ocupada) continue;
    const siguiente = deLaCapa
      .filter((c) => c.startTime > t)
      .reduce((min, c) => Math.min(min, c.startTime), Infinity);
    const lugar = siguiente - t;
    if (
      !mejor ||
      lugar > mejor.lugar ||
      (lugar === mejor.lugar && trackIndex < mejor.trackIndex)
    ) {
      mejor = { trackIndex, lugar };
    }
  }
  if (!mejor || mejor.lugar < (params.duracion ?? 0) - EPS) return null;
  return mejor.trackIndex;
}
